// =====================================================================
// USER ROLES — single source of truth for the whole admin UI.
// Mirrors the backend USER_ROLES enum (hexa: src/users/enum/role.enum.ts).
// Every role picker, label and access check reads from here — add a role in
// this one place and it flows everywhere. Nothing else hardcodes role strings.
// =====================================================================

// Ordered role codes (the order pickers display them in). Keep in sync with the
// backend enum. INVENTORY_DEPARTMENT is a legacy role still assigned to existing
// users, so it is retained here so those users are never orphaned.
export const ROLE_CODES = [
  'SUPER_ADMIN',
  'ADMIN',
  'AREA_MANAGER',
  'STORE_MANAGER',
  'PLANNING_TEAM',
  'SALES_STAFF',
  'PRODUCT_MANAGER',
  'CARREFOUR',
  'TECHNICIAN',
  'PROJECT_MANAGER',
  'STOREKEEPER',
  'MERCHANDISE_ANALYST',
  'SUPERVISOR',
  'INVENTORY_DEPARTMENT',
  'INVENTORY_HEAD',
  // Marketing & creative
  'DGM_MARKETING',
  'AGM_MARKETING',
  'SENIOR_GRAPHIC_DESIGNER',
  'GRAPHIC_DESIGNER',
  'MARKETING_EXECUTIVE',
  'INHOUSE_PRINTER',
];

// Convenient name → code map (e.g. USER_ROLES.SUPER_ADMIN).
export const USER_ROLES = ROLE_CODES.reduce((m, c) => {
  m[c] = c;
  return m;
}, {});

// code → display label ("STORE_MANAGER" → "STORE MANAGER"). A single formatter
// so labels are consistent everywhere; unknown codes still render readably.
export const roleLabel = (code) => (code ? String(code).replace(/_/g, ' ') : '');

// code → label map.
export const ROLE_LABELS = ROLE_CODES.reduce((m, c) => {
  m[c] = roleLabel(c);
  return m;
}, {});

// {value,label} / {roleCode,roleLabel} option list — covers every picker shape
// used across the app (Select, Autocomplete, custom lists).
export const ROLE_OPTIONS = ROLE_CODES.map((code) => ({
  value: code,
  code,
  roleCode: code,
  label: roleLabel(code),
  roleLabel: roleLabel(code),
}));

// Roles granted admin-level UI access (admin menus + admin-only routes).
export const ADMIN_ROLES = ['SUPER_ADMIN', 'ADMIN'];

// Is this role admin / super-admin?
export const isAdminRole = (role) => ADMIN_ROLES.includes(role);

// Excel report access, set per stage (and for requesters) in the workflow
// builder's Handlers step. The backend resolves it per viewer and returns it as
// workflow.reportAccess (hexa: src/workflow/report/report-access.ts).
export const REPORT_WORKFLOW_IDS = ['DAMAGE_DECLARATION'];
export const REPORT_ACCESS_OPTIONS = [
  { value: 'NONE', label: 'No access' },
  { value: 'OWN_STORES', label: 'See & download — own stores' },
  { value: 'ALL_STORES', label: 'See & download — all stores' },
];
